import React, { useState, useEffect, useRef } from 'react';
import { CreditCard, Shield, Lock, AlertCircle, Loader2, ArrowRight } from 'lucide-react';
import { recurringPaymentsApi } from '../../../../services/recurringPaymentsApi';
import { ensureEpaycoSession, loadEpaycoScript } from '../../paymentsApi';
import { PaymentMethodType } from '../../../../types/payments';
import { toast } from 'sonner';
import { cardExpiryParts, formatCardExpiry } from '../../cardExpiry';
import { ERROR_MESSAGES } from '../../../../lib/userErrors';

type IntlDocType = 'PPN' | 'DNI';

interface InternationalOneTimeCardFormProps {
    amount: number;
    planType: string;
    currency: string;
    minRecharge: number;
    countryOverride?: string;
    onSuccess: (messages: number, transactionId: string) => void;
}

function maskCard(number: string): string {
    const clean = number.replace(/\D/g, '');
    if (clean.length < 4) return '****';
    return `${clean.slice(0, 6)}${'*'.repeat(Math.max(0, clean.length - 10))}${clean.slice(-4)}`;
}

function detectFranchise(digits: string): PaymentMethodType | null {
    if (digits.startsWith('4')) return 'VISA';
    if (/^5[1-5]/.test(digits) || /^2[2-7]/.test(digits)) return 'MASTERCARD';
    if (/^3[47]/.test(digits)) return 'AMEX';
    return null;
}

function keepDigits(input: HTMLInputElement, maxLength: number) {
    input.value = input.value.replace(/\D/g, '').slice(0, maxLength);
}

export function InternationalOneTimeCardForm({
    amount,
    planType,
    currency,
    minRecharge,
    countryOverride,
    onSuccess,
}: InternationalOneTimeCardFormProps) {
    const formRef = useRef<HTMLFormElement>(null);
    const [publicKey, setPublicKey] = useState<string | null>(null);
    const [loadingKey, setLoadingKey] = useState(true);
    // Card fields are uncontrolled so PAN/CVV never end up as DOM attributes.
    const cardNumberRef = useRef<HTMLInputElement>(null);
    const expiryMonthRef = useRef<HTMLInputElement>(null);
    const expiryYearRef = useRef<HTMLInputElement>(null);
    const cvvRef = useRef<HTMLInputElement>(null);
    const [cardHolder, setCardHolder] = useState('');
    const [identificationType, setIdentificationType] = useState<IntlDocType>('PPN');
    const [identificationNumber, setIdentificationNumber] = useState('');
    const [expiryDisplay, setExpiryDisplay] = useState('');
    const [expiryMonth, setExpiryMonth] = useState('');
    const [expiryYear, setExpiryYear] = useState('');
    const [paymentMethod, setPaymentMethod] = useState<PaymentMethodType>('VISA');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const load = async () => {
            try {
                await loadEpaycoScript();
                const { public_key } = await recurringPaymentsApi.getEpaycoPublicKey();
                setPublicKey(public_key);
                window.ePayco?.setPublicKey(public_key);
            } catch (err) {
                console.error(err);
                setError('No se pudo inicializar ePayco.');
            } finally {
                setLoadingKey(false);
            }
        };
        load();
    }, []);

    const applyExpiry = (raw: string) => {
        const display = formatCardExpiry(raw);
        const parts = cardExpiryParts(display);
        setExpiryDisplay(display);
        setExpiryMonth(parts?.month ?? display.replace(/\D/g, '').slice(0, 2));
        setExpiryYear(parts?.year ?? '');
    };

    const clearCardFields = () => {
        [cardNumberRef, cvvRef].forEach(ref => {
            if (ref.current) ref.current.value = '';
        });
        setExpiryDisplay('');
        setExpiryMonth('');
        setExpiryYear('');
    };

    const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const input = e.currentTarget;
        const digits = input.value.replace(/\D/g, '').substring(0, 16);
        input.value = digits.match(/.{1,4}/g)?.join(' ') || digits;
        const franchise = detectFranchise(digits);
        if (franchise) setPaymentMethod(franchise);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (amount < minRecharge) {
            toast.warning('Monto insuficiente', {
                description: `El monto mínimo es de ${minRecharge} ${currency}.`,
            });
            return;
        }
        if (!publicKey || !window.ePayco?.token || !formRef.current) {
            setError('ePayco no está listo.');
            return;
        }

        const cardDigits = (cardNumberRef.current?.value ?? '').replace(/\D/g, '');
        const expiry = cardExpiryParts(expiryDisplay);
        const docMin = identificationType === 'PPN' ? 4 : 1;
        if (!expiry) {
            setError(ERROR_MESSAGES.card_tokenize_failed);
            return;
        }
        if (identificationNumber.length < docMin) {
            setError(identificationType === 'PPN'
                ? 'El pasaporte debe tener entre 4 y 12 caracteres.'
                : 'Ingresa el documento nacional.');
            return;
        }

        setLoading(true);
        setError(null);
        window.ePayco.setPublicKey(publicKey);
        ensureEpaycoSession();

        window.ePayco.token.create(formRef.current, async (tokenError, token) => {
            if (tokenError || !token) {
                if (cvvRef.current) cvvRef.current.value = '';
                setError(ERROR_MESSAGES.card_tokenize_failed);
                setLoading(false);
                return;
            }
            clearCardFields();
            try {
                const method = await recurringPaymentsApi.registerCard({
                    epayco_token: token,
                    card_holder_name: cardHolder,
                    identification_number: identificationNumber,
                    identification_type: identificationType,
                    payment_method: paymentMethod,
                    expiration_date: `${expiry.year}/${expiry.month}`,
                    masked_number: maskCard(cardDigits),
                    set_as_default: true,
                });

                const res = await recurringPaymentsApi.chargeInternational({
                    amount,
                    plan_type: planType,
                }, countryOverride);

                if (res.success) {
                    toast.success('¡Pago exitoso!');
                    onSuccess(0, res.transaction_id || method.id);
                } else {
                    setError('Pago rechazado.');
                }
            } catch (err: any) {
                setError(err.response?.data?.detail || 'Pago rechazado. Verifica los datos.');
            } finally {
                setLoading(false);
            }
        });
    };

    if (loadingKey) {
        return (
            <div className="p-8 flex flex-col items-center text-slate-400">
                <Loader2 className="w-8 h-8 animate-spin mb-4 text-[#1CB3B3]" />
                <p>Conectando con ePayco...</p>
            </div>
        );
    }

    return (
        <div className="w-full">
            <div className="flex items-center gap-2 mb-4 text-slate-500 text-sm">
                <Lock className="w-4 h-4 text-[#1CB3B3]" />
                Tokenización ePayco — el número de tarjeta no se envía a Reveca
            </div>
            {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm flex gap-2">
                    <AlertCircle className="w-4 h-4 shrink-0" />
                    {error}
                </div>
            )}
            <form ref={formRef} onSubmit={handleSubmit} className="space-y-3">
                <input
                    required
                    data-epayco="card[name]"
                    placeholder="Nombre del titular de la tarjeta"
                    value={cardHolder}
                    onChange={e => setCardHolder(e.target.value)}
                    autoComplete="cc-name"
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#1CB3B3]/20 focus:border-[#1CB3B3]"
                />
                <input
                    required
                    data-epayco="card[number]"
                    placeholder="Número de tarjeta"
                    ref={cardNumberRef}
                    onChange={handleCardNumberChange}
                    autoComplete="cc-number"
                    inputMode="numeric"
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#1CB3B3]/20 focus:border-[#1CB3B3]"
                />
                <input type="hidden" data-epayco="card[exp_month]" value={expiryMonth} ref={expiryMonthRef} />
                <input type="hidden" data-epayco="card[exp_year]" value={expiryYear} ref={expiryYearRef} />
                <div className="grid grid-cols-2 gap-2">
                    <input
                        required
                        placeholder="MM/AA"
                        value={expiryDisplay}
                        onChange={e => applyExpiry(e.target.value)}
                        autoComplete="cc-exp"
                        inputMode="numeric"
                        maxLength={5}
                        className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#1CB3B3]/20 focus:border-[#1CB3B3]"
                    />
                    <input
                        required
                        data-epayco="card[cvc]"
                        placeholder="Código de seguridad"
                        ref={cvvRef}
                        onChange={e => keepDigits(e.currentTarget, 4)}
                        autoComplete="cc-csc"
                        inputMode="numeric"
                        className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#1CB3B3]/20 focus:border-[#1CB3B3]"
                    />
                </div>
                <input type="hidden" data-epayco="card[email]" value="intl@reveca.io" />
                <div className="grid grid-cols-[7.5rem_1fr] gap-2">
                    <select
                        value={identificationType}
                        onChange={e => {
                            setIdentificationType(e.target.value as IntlDocType);
                            setIdentificationNumber('');
                        }}
                        className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-3 text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#1CB3B3]/20 focus:border-[#1CB3B3]"
                    >
                        <option value="PPN">Pasaporte</option>
                        <option value="DNI">Documento nacional</option>
                    </select>
                    <input
                        required
                        placeholder={identificationType === 'PPN' ? 'Pasaporte' : 'Documento nacional'}
                        value={identificationNumber}
                        onChange={e => setIdentificationNumber(
                            e.target.value.replace(/[^a-zA-Z0-9]/g, '').slice(0, identificationType === 'PPN' ? 12 : 20)
                        )}
                        autoComplete="off"
                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#1CB3B3]/20 focus:border-[#1CB3B3]"
                    />
                </div>
                <button
                    type="submit"
                    disabled={loading}
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-[#1CB3B3] hover:bg-[#169a9a] text-white font-bold disabled:opacity-50"
                >
                    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                        <>
                            <Shield className="w-4 h-4" />
                            Pagar {amount} {currency}
                            <ArrowRight className="w-4 h-4" />
                        </>
                    )}
                </button>
                <p className="text-center text-[11px] text-slate-500 flex items-center justify-center gap-1">
                    <CreditCard className="w-3 h-3" /> Procesado por ePayco
                </p>
            </form>
        </div>
    );
}
